'use client'

import Image from "next/image";
import googleReviewLogo from "@/assets/images/logos/reviews-google-logo.svg";
import { useEffect, useState } from "react";
import { fetchMapsReview } from "@/utils/client/client_api";
import { MapsData } from "@/types/typesInterfaces";
import { Swiper, SwiperSlide, } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';

import { useTranslations } from 'next-intl';

import 'swiper/css';
import "swiper/css/autoplay";
import "swiper/css/pagination";
import { DEFAULT_REVIEWS_NUMBER, DEFAULT_REVIEWS_RATING } from "@/utils/constants";

type Review = {
  authorName: string,
  rating: number,
  text: string,
  link: string,
}

const GoogleReview = () => {
  const t = useTranslations('GoogleReview');
  const reviewList: Review[] = t.raw('list') as Review[];

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reviews, setReviews] = useState<MapsData>();

  const chosenReviews: Review[] = reviewList.map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value)

  const fiveStar = [1, 2, 3, 4, 5];

  const getLastReviews = async () => {
    setIsLoading(true);
    try {
      const resp = await fetchMapsReview();
      if (resp.isOk) {
        setReviews(resp.response);
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getLastReviews();
  }, [])

  const getSvgPath = () => {
    return <path d="M11.2827 3.4533C11.5131 2.98636 11.6284 2.75289 11.7848 2.6783C11.9209 2.6134 12.0791 2.6134 12.2152 2.6783C12.3717 2.75289 12.4869 2.98636 12.7174 3.4533L14.9041 7.88327C14.9721 8.02112 15.0061 8.09004 15.0558 8.14356C15.0999 8.19094 15.1527 8.22933 15.2113 8.25661C15.2776 8.28741 15.3536 8.29852 15.5057 8.32076L20.397 9.03569C20.9121 9.11098 21.1696 9.14862 21.2888 9.27442C21.3925 9.38388 21.4412 9.53428 21.4215 9.68376C21.3988 9.85556 21.2124 10.0372 20.8395 10.4004L17.3014 13.8464C17.1912 13.9538 17.136 14.0075 17.1004 14.0714C17.0689 14.128 17.0487 14.1902 17.0409 14.2545C17.0321 14.3271 17.0451 14.403 17.0711 14.5547L17.906 19.4221C17.994 19.9355 18.038 20.1922 17.9553 20.3445C17.8833 20.477 17.7554 20.57 17.6071 20.5975C17.4366 20.6291 17.2061 20.5078 16.7451 20.2654L12.3724 17.9658C12.2361 17.8942 12.168 17.8583 12.0962 17.8443C12.0327 17.8318 11.9673 17.8318 11.9038 17.8443C11.832 17.8583 11.7639 17.8942 11.6277 17.9658L7.25492 20.2654C6.79392 20.5078 6.56341 20.6291 6.39297 20.5975C6.24468 20.57 6.11672 20.477 6.04474 20.3445C5.962 20.1922 6.00603 19.9355 6.09407 19.4221L6.92889 14.5547C6.95491 14.403 6.96793 14.3271 6.95912 14.2545C6.95132 14.1902 6.93111 14.128 6.89961 14.0714C6.86402 14.0075 6.80888 13.9538 6.69859 13.8464L3.16056 10.4004C2.78766 10.0372 2.60121 9.85556 2.57853 9.68376C2.55879 9.53428 2.60755 9.38388 2.71125 9.27442C2.83044 9.14862 3.08797 9.11098 3.60304 9.03569L8.49431 8.32076C8.64642 8.29852 8.72248 8.28741 8.78872 8.25661C8.84736 8.22933 8.90016 8.19094 8.94419 8.14356C8.99391 8.09004 9.02793 8.02112 9.09597 7.88327L11.2827 3.4533Z" fill="#FFC627" stroke="#FFC627" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  }

  const starsElement = (stars: number) => {
    const starsElements = [];

    for (let index = 0; index < stars; index++) {
      starsElements.push(
        <svg
          key={index}
          className="w-5 h-5 text-yellow-400"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          {getSvgPath()}
        </svg>
      )
    }

    return starsElements;
  }

  const reviewElement = (review: Review) => {
    return <div className="mt-4">
      <div className="flex justify-between items-center mb-2">
        <div className="font-bold text-xl text-blue-950">
          <a className="no-underline" href={review.link} target="_blank" rel="noopener noreferrer">
            {review.authorName}
          </a>
        </div>
        <div className="flex">
          {starsElement(review.rating)}
        </div>
      </div>

      <p className="text-blue-950 text-sm">
        {review.text}
      </p>

      {/* Review progress bar */}
      {/* <div className="mt-6 w-full bg-gray-100 rounded-full h-2.5">
          <div className="bg-gray-300 h-2.5 rounded-full w-1/2"></div>
        </div> */}
    </div>
  }

  return (
    <div className={`w-full p-8`}>

      {/* header */}
      <div className="flex items-center mb-4">
        <div className="flex">
          <Image className="w-40" src={googleReviewLogo} alt="google reviews logo" />
        </div>
      </div>

      <div className={`flex flex-wrap items-center gap-4 ${isLoading ? 'blur-xs' : ''}`}>
        <div className="flex gap-2 items-center">
          {/* Stars */}
          <div className="flex">
            {fiveStar.map((star, index) => (
              <svg
                key={index}
                className={`w-6 h-6 ${index < 4.5 ? "text-yellow-400" : "text-gray-300"
                  }`}
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                {getSvgPath()}
              </svg>
            ))}
          </div>
          <div className="text-xl font-bold text-blue-950">{reviews == null ? DEFAULT_REVIEWS_RATING : reviews.rating.toString()}</div>
        </div>
        <div className="w-px h-6 border"></div>
        <div className="text-blue-950 text-xl font-semibold underline">
          <a href="https://www.google.com/maps/place/Habacus+S.r.l./@45.4630909,9.1770024,17z/data=!4m8!3m7!1s0x4786c7f5a823900f:0x160caa9a6ca4228!8m2!3d45.4630909!4d9.1795773!9m1!1b1!16s%2Fg%2F11q94pzgzy" target="_blank" rel="noopener noreferrer">
            {reviews == null ? DEFAULT_REVIEWS_NUMBER : reviews.userRatingsTotal.toString()} {t('reviews')}
          </a>
        </div>
      </div>

      <hr className="my-4" />

      {/* Review */}
      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
        }}
        navigation={true}
        pagination={{
          type: "progressbar",
        }}
        loop={true} >
        {
          chosenReviews.map((review, i) => {
            return <SwiperSlide key={"review_" + i}>{reviewElement(review)}</SwiperSlide>
          })
        }
      </Swiper>
    </div >
  );
};

export default GoogleReview;
